import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Box, Button, ButtonBase, Card, Dialog, DialogActions, DialogContent, DialogTitle,
  Menu, MenuItem, Stack, Tooltip, Typography,
} from '@mui/material';
import { EditOutlined as EditIcon, PlaceOutlined as AreaIcon } from '@mui/icons-material';
import AccessService, { MatrixView, PersonRow, describeAccessError } from '../../api/services/access.service';
import type { AccessArea, AccessCell } from '../../access/areas';
import { atLeast } from '../../access/areas';
import {
  AREA_GROUPS, LEVEL_COLOR, LEVEL_LABEL, ROLE_NAME, cellOptions, cellValue, describeCell, sameCell,
} from '../../access/labels';
import PageBody from '../../components/ui/PageBody';
import PageHeader from '../../components/ui/PageHeader';
import { EmptyState, LoadingState } from '../../components/ui/States';
import {
  GROUP_ROW_HEIGHT, HEAD_BG, HEAD_ROW_HEIGHT, HEAD_TEXT, ROW_HEIGHT, TWO_LINE_ROW_HEIGHT,
} from '../../components/ui/tableStyle';
import SettingsPanel from './SettingsPanel';

// Settings → Access: who can see and change each part of TopoSDS.
//
// Names down the side — the roles, then every person — and the parts of
// TopoSDS across the top. A role's row sets everyone in that role; a
// person's row differs from their role only where it says so.
//
// Read-only until Edit; Save asks first and lists what will change. The
// server checks everything again (backend ACCESS_MATRIX.md); choices it
// would refuse are greyed here before you try.

type Overrides = MatrixView['overrides'];
type Exceptions = Record<string, Partial<Record<AccessArea, AccessCell>>>; // accountId -> area -> cell

const ROLES = ['AO', 'CA', 'BC', 'Driver', 'Maintainer'];
const COLUMNS = AREA_GROUPS.flatMap((g) => g.areas);
const GROUP_STARTS = new Set(AREA_GROUPS.slice(1).map((g) => g.areas[0].key));
const NAME_W = 220;
const CELL_MIN_W = 84;

// The site's table header (components/ui/tableStyle), for both header rows.
const HEAD = { ...HEAD_TEXT, bgcolor: HEAD_BG };
const RULE = { borderBottom: '1px solid', borderBottomColor: 'divider' };
const groupRule = (area: AccessArea) => (GROUP_STARTS.has(area) ? { borderLeft: '1px solid', borderLeftColor: 'divider' } : {});

const toExceptions = (people: PersonRow[]): Exceptions => Object.fromEntries(
  people.map((p) => [p.accountId, Object.fromEntries(p.exceptions.map((e) => [e.area, { level: e.level, scope: e.scope }]))]),
);

const AccessSettings: React.FC = () => {
  const [view, setView] = useState<MatrixView | null>(null);
  const [people, setPeople] = useState<PersonRow[]>([]);
  const [draft, setDraft] = useState<Overrides>({});
  const [exceptions, setExceptions] = useState<Exceptions>({});
  const [editing, setEditing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; area: AccessArea; role?: string; personId?: string } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [denied, setDenied] = useState(false);

  const load = useCallback(async () => {
    try {
      const [v, list] = await Promise.all([AccessService.matrix(), AccessService.people()]);
      setView(v);
      setDraft(v.overrides || {});
      setPeople(list);
      setExceptions(toExceptions(list));
      setError(null);
    } catch (err: any) {
      if (err?.response?.status === 403) setDenied(true);
      else setError(describeAccessError(err, 'The access settings could not be loaded.'));
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const scopedOf = useMemo(
    () => Object.fromEntries((view?.areas || []).map((a) => [a.key, a.scoped])) as Record<AccessArea, boolean>,
    [view],
  );
  const labelOf = (key: AccessArea) => view?.areas.find((a) => a.key === key)?.label || key;

  const roleCell = useCallback((role: string, area: AccessArea, overrides?: Overrides): AccessCell => {
    const source = overrides === undefined ? draft : overrides;
    return source?.[area]?.[role] || view!.defaults[role][area];
  }, [draft, view]);
  const personCell = (p: PersonRow, area: AccessArea): AccessCell =>
    (p.role === 'AO' ? view!.defaults.AO[area] : exceptions[p.accountId]?.[area] || roleCell(p.role, area));

  // Every change since the last save, in words — the Save dialog lists them.
  const changes = useMemo(() => {
    if (!view) return [] as string[];
    const out: string[] = [];
    for (const role of view.editableRoles) {
      for (const { key } of COLUMNS) {
        const now = roleCell(role, key);
        const was = roleCell(role, key, view.overrides);
        if (!sameCell(now, was, scopedOf[key])) {
          out.push(`${ROLE_NAME[role]} · ${labelOf(key)}: ${describeCell(was, scopedOf[key])} → ${describeCell(now, scopedOf[key])}`);
        }
      }
    }
    const before = toExceptions(people);
    for (const p of people) {
      for (const { key } of COLUMNS) {
        const now = exceptions[p.accountId]?.[key];
        const was = before[p.accountId]?.[key];
        if (JSON.stringify(now || null) === JSON.stringify(was || null)) continue;
        const word = (c?: AccessCell) => (c ? describeCell(c, scopedOf[key]) : 'same as role');
        out.push(`${p.name} · ${labelOf(key)}: ${word(was)} → ${word(now)}`);
      }
    }
    return out;
  }, [view, roleCell, scopedOf, people, exceptions]); // eslint-disable-line react-hooks/exhaustive-deps

  const changedPeople = useMemo(() => {
    const before = toExceptions(people);
    return people.filter((p) => JSON.stringify(exceptions[p.accountId] || {}) !== JSON.stringify(before[p.accountId] || {}));
  }, [people, exceptions]);
  const rolesChanged = useMemo(
    () => !!view && JSON.stringify(draft || {}) !== JSON.stringify(view.overrides || {}),
    [draft, view],
  );
  const anyOverride = useMemo(() => Object.values(draft || {}).some((byRole) => Object.keys(byRole || {}).length), [draft]);

  const setRoleCell = (area: AccessArea, role: string, cell: AccessCell) => {
    setDraft((prev) => {
      const next: Overrides = JSON.parse(JSON.stringify(prev || {}));
      if (sameCell(cell, view!.defaults[role][area], scopedOf[area])) {
        if (next[area]) delete next[area]![role];
        if (next[area] && !Object.keys(next[area]!).length) delete next[area];
      } else {
        next[area] = { ...(next[area] || {}), [role]: cell };
      }
      return next;
    });
  };

  const setPersonCell = (personId: string, area: AccessArea, cell: AccessCell | null) => {
    setExceptions((prev) => {
      const mine = { ...(prev[personId] || {}) };
      if (cell) mine[area] = cell; else delete mine[area];
      return { ...prev, [personId]: mine };
    });
  };

  const startEditing = () => { setSaved(null); setError(null); setEditing(true); };
  const cancel = () => {
    setDraft(view?.overrides || {});
    setExceptions(toExceptions(people));
    setEditing(false);
    setError(null);
  };

  const save = async () => {
    setBusy(true);
    setError(null);
    const count = changes.length;
    try {
      if (rolesChanged) await AccessService.saveMatrix(draft);
      for (const p of changedPeople) {
        const list = Object.entries(exceptions[p.accountId] || {}).map(([area, cell]) => ({ area: area as AccessArea, ...(cell as AccessCell) }));
        await AccessService.savePerson(p.accountId, list);
      }
      await load();
      setConfirming(false);
      setEditing(false);
      setSaved(`Saved ${count} change${count === 1 ? '' : 's'}. They apply within seconds.`);
    } catch (err) {
      setConfirming(false);
      setError(describeAccessError(err, 'Those changes could not be saved.'));
    } finally {
      setBusy(false);
    }
  };

  // Every role cell back to its default, except the owner's own access
  // column, which only the owner moves. People's exceptions stay.
  const restore = () => {
    setDraft((prev) => (view?.canEditAccessRow ? {} : (prev?.access ? { access: prev.access } : {})));
  };

  // Nobody grants more than they have.
  const tooMuch = (area: AccessArea, cell: AccessCell) => {
    const mine = view!.yours[area];
    return !atLeast(mine.level, cell.level)
      || (scopedOf[area] && cell.scope === 'company' && mine.scope === 'area' && cell.level !== 'none');
  };

  if (denied) {
    return (
      <PageBody>
        <SettingsPanel />
        <EmptyState title="Access" description="Only the owner, or a company admin the owner allows, can change who sees what." />
      </PageBody>
    );
  }

  const renderCell = (
    key: string,
    cell: AccessCell,
    area: AccessArea,
    {
      row, height, editable, differs, unsaved, onOpen,
    }: { row: string; height: number; editable: boolean; differs?: boolean; unsaved?: boolean; onOpen?: (el: HTMLElement) => void },
  ) => {
    const content = (
      <Stack direction="row" alignItems="center" justifyContent="center" gap={0.25}>
        <Typography variant="body2" sx={{ color: LEVEL_COLOR[cell.level], fontWeight: differs ? 700 : 500, fontSize: 13 }}>
          {cell.level === 'none' ? '—' : LEVEL_LABEL[cell.level]}
        </Typography>
        {scopedOf[area] && cell.level !== 'none' && cell.scope === 'area' && (
          <Tooltip title="Own service area only"><AreaIcon sx={{ fontSize: 14, color: 'text.secondary' }} /></Tooltip>
        )}
      </Stack>
    );
    // `height` is the row's; the cell's bottom rule takes one pixel of it.
    const live = editing && editable;
    return (
      <Box
        component="td"
        key={key}
        sx={{ minWidth: CELL_MIN_W, p: 0, textAlign: 'center', ...RULE, ...groupRule(area), bgcolor: unsaved ? 'action.selected' : undefined }}
      >
        {live ? (
          <ButtonBase
            onClick={(e) => onOpen?.(e.currentTarget)}
            aria-label={`${row}, ${labelOf(area)}: ${describeCell(cell, scopedOf[area])}`}
            sx={{ width: '100%', height: height - 1, borderRadius: 1, '&:hover': { bgcolor: 'action.hover' } }}
          >
            {content}
          </ButtonBase>
        ) : (
          <Box sx={{ height: height - 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: editing && !editable ? 0.5 : 1 }}>{content}</Box>
        )}
      </Box>
    );
  };

  const nameCell = (title: string, sub?: string) => (
    <Box
      component="th"
      scope="row"
      sx={{
        position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', textAlign: 'left', fontWeight: 400,
        width: NAME_W, minWidth: NAME_W, px: 2, ...RULE, borderRight: '1px solid', borderRightColor: 'divider',
      }}
    >
      <Typography variant="body2" fontWeight={600} noWrap>{title}</Typography>
      {sub && <Typography variant="caption" color="text.secondary" noWrap component="div">{sub}</Typography>}
    </Box>
  );

  const sectionRow = (label: string) => (
    <Box component="tr" key={`section-${label}`}>
      <Box component="td" colSpan={COLUMNS.length + 1} sx={{ ...HEAD, px: 2, py: 0.75, ...RULE }}>
        <Box component="span" sx={{ position: 'sticky', left: 16 }}>{label}</Box>
      </Box>
    </Box>
  );

  // The open menu's options.
  const menuItems = () => {
    if (!menu || !view) return null;
    const { area, role, personId } = menu;
    const scoped = scopedOf[area];
    const person = personId ? people.find((p) => p.accountId === personId) : null;
    const own = person ? exceptions[person.accountId]?.[area] : undefined;
    const current = person ? personCell(person, area) : roleCell(role!, area);
    const items: React.ReactNode[] = [];
    if (person) {
      items.push(
        <MenuItem key="same" selected={!own} onClick={() => { setPersonCell(person.accountId, area, null); setMenu(null); }}>
          <Typography variant="body2" color="text.secondary">
            Same as {ROLE_NAME[person.role]} · {describeCell(roleCell(person.role, area), scoped)}
          </Typography>
        </MenuItem>,
      );
    }
    for (const opt of cellOptions(scoped)) {
      // For a person, the role's own level is the "Same as" line above.
      if (person && sameCell(opt.cell, roleCell(person.role, area), scoped)) continue;
      items.push(
        <MenuItem
          key={opt.value}
          disabled={tooMuch(area, opt.cell)}
          selected={(!person || !!own) && cellValue(current, scoped) === opt.value}
          onClick={() => {
            if (person) setPersonCell(person.accountId, area, opt.cell); else setRoleCell(area, role!, opt.cell);
            setMenu(null);
          }}
        >
          <Typography variant="body2" sx={{ color: LEVEL_COLOR[opt.cell.level] }}>{opt.label}</Typography>
        </MenuItem>,
      );
    }
    return items;
  };

  const actions = view && (editing ? (
    <Stack direction="row" gap={1}>
      {anyOverride && <Button onClick={restore} disabled={busy}>Restore defaults</Button>}
      <Button onClick={cancel} disabled={busy}>Cancel</Button>
      <Button variant="contained" disableElevation onClick={() => setConfirming(true)} disabled={busy || changes.length === 0}>
        {changes.length ? `Save ${changes.length} change${changes.length === 1 ? '' : 's'}` : 'Save'}
      </Button>
    </Stack>
  ) : (
    <Button variant="outlined" startIcon={<EditIcon />} onClick={startEditing}>Edit</Button>
  ));

  return (
    <PageBody fluid>
      <SettingsPanel />
      <PageHeader title="Access" subtitle="Who can see and change each part of TopoSDS." actions={actions} />

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {saved && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSaved(null)}>{saved}</Alert>}

      {!view ? <LoadingState /> : (
        <Card sx={{ overflow: 'hidden' }}>
          {/* Scrolls inside itself, both ways: the headers stay at the top
              and the names stay at the left. */}
          <Box sx={{ overflow: 'auto', maxHeight: 'calc(100vh - 230px)' }}>
            <Box component="table" sx={{ borderCollapse: 'separate', borderSpacing: 0, width: '100%' }}>
              <Box component="thead">
                <Box component="tr">
                  <Box
                    component="th"
                    rowSpan={2}
                    sx={{
                      ...HEAD, position: 'sticky', top: 0, left: 0, zIndex: 4, width: NAME_W, minWidth: NAME_W,
                      ...RULE, borderRight: '1px solid', borderRightColor: 'divider',
                    }}
                  />
                  {AREA_GROUPS.map((g, i) => (
                    <Box
                      component="th"
                      key={g.label}
                      colSpan={g.areas.length}
                      sx={{
                        ...HEAD, position: 'sticky', top: 0, zIndex: 3, height: GROUP_ROW_HEIGHT, textAlign: 'center',
                        ...(i ? { borderLeft: '1px solid', borderLeftColor: 'divider' } : {}),
                      }}
                    >
                      {g.label}
                    </Box>
                  ))}
                </Box>
                <Box component="tr">
                  {COLUMNS.map((a) => (
                    <Box
                      component="th"
                      key={a.key}
                      sx={{
                        ...HEAD, position: 'sticky', top: GROUP_ROW_HEIGHT, zIndex: 3, height: HEAD_ROW_HEIGHT, px: 1, textAlign: 'center',
                        ...RULE, ...groupRule(a.key),
                      }}
                    >
                      <Tooltip title={a.hint}><span>{a.short}</span></Tooltip>
                    </Box>
                  ))}
                </Box>
              </Box>
              <Box component="tbody">
                {sectionRow('Roles')}
                {ROLES.map((role) => (
                  <Box component="tr" key={role}>
                    {nameCell(ROLE_NAME[role])}
                    {COLUMNS.map(({ key }) => {
                      const cell = roleCell(role, key);
                      // The owner's row is fixed; the access column is the owner's to set, and only for CAs.
                      const editable = role !== 'AO' && !(key === 'access' && (!view.canEditAccessRow || role !== 'CA'));
                      return renderCell(key, cell, key, {
                        row: ROLE_NAME[role],
                        height: ROW_HEIGHT,
                        editable,
                        differs: role !== 'AO' && !sameCell(cell, view.defaults[role][key], scopedOf[key]),
                        unsaved: role !== 'AO' && !sameCell(cell, roleCell(role, key, view.overrides), scopedOf[key]),
                        onOpen: (anchor) => setMenu({ anchor, area: key, role }),
                      });
                    })}
                  </Box>
                ))}

                {people.length > 0 && sectionRow('People')}
                {people.map((p) => {
                  const before = toExceptions([p])[p.accountId] || {};
                  return (
                    <Box component="tr" key={p.accountId}>
                      {nameCell(p.name, `${ROLE_NAME[p.role]}${p.status !== 'active' ? ` · ${p.status}` : ''}`)}
                      {COLUMNS.map(({ key }) => {
                        const own = exceptions[p.accountId]?.[key];
                        const editable = p.editable && !(key === 'access' && (!view.canEditAccessRow || p.role !== 'CA'));
                        return renderCell(key, personCell(p, key), key, {
                          row: p.name,
                          height: TWO_LINE_ROW_HEIGHT,
                          editable,
                          differs: !!own,
                          unsaved: JSON.stringify(own || null) !== JSON.stringify(before[key] || null),
                          onOpen: (anchor) => setMenu({ anchor, area: key, personId: p.accountId }),
                        });
                      })}
                    </Box>
                  );
                })}
              </Box>
            </Box>
          </Box>
        </Card>
      )}

      <Menu anchorEl={menu?.anchor} open={!!menu} onClose={() => setMenu(null)} MenuListProps={{ dense: true }}>
        {menuItems()}
      </Menu>

      <Dialog open={confirming} onClose={busy ? undefined : () => setConfirming(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Save {changes.length} change{changes.length === 1 ? '' : 's'}?</DialogTitle>
        <DialogContent>
          <Stack gap={0.75}>
            {changes.map((line) => <Typography key={line} variant="body2">{line}</Typography>)}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirming(false)} disabled={busy}>Back</Button>
          <Button variant="contained" disableElevation onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
        </DialogActions>
      </Dialog>
    </PageBody>
  );
};

export default AccessSettings;
